import React from 'react';
import type { TradingTrade } from '../../types/trading';
import MobileDataCard from '../mobile/MobileDataCard';

interface TradeTableProps {
  trades: TradingTrade[];
}

const TradeTable: React.FC<TradeTableProps> = ({ trades }) => {
  const formatDate = (ts: string | null) => {
    if (!ts) return '—';
    let normalized = ts.replace(/^(\d{4})\.(\d{2})\.(\d{2})/, '$1-$2-$3');
    if (!/[Z+-]\d{0,4}$/.test(normalized)) normalized += 'Z';
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const pnlColor = (pnl: number | null) => {
    if (pnl === null || pnl === undefined) return 'text-slate-400';
    if (pnl > 0) return 'text-emerald-400';
    if (pnl < 0) return 'text-red-400';
    return 'text-slate-400';
  };

  const formatReason = (reason: string | null) => {
    if (!reason) return '—';
    // Split "ea_closed|[sl] CandlePattern" into parts
    const parts = reason.split('|');
    const code = parts[0];
    const comment = parts[1] || '';
    const labels: Record<string, string> = {
      sl_hit: 'SL Hit',
      tp_hit: 'TP Hit',
      stop_out: 'Stop Out',
      manual: 'Manual',
      ea_closed: 'EA',
      other: 'Other',
    };
    const label = labels[code] || code;
    return comment ? `${label} — ${comment}` : label;
  };

  if (trades.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        No trades synced yet. Connect your MT5 EA to start syncing trades.
      </div>
    );
  }

  return (
    <>
      {/* Mobile card view */}
      <div className="space-y-3 lg:hidden">
        {trades.map((trade) => (
          <MobileDataCard
            key={trade.ticket}
            fields={[
              {
                label: 'Ticket',
                value: <span className="text-slate-300">#{trade.ticket}</span>,
              },
              {
                label: 'Symbol',
                value: <span className="font-semibold text-white">{trade.symbol}</span>,
              },
              {
                label: 'Direction',
                value: (
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      trade.direction === 'BUY'
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'bg-red-500/10 text-red-400'
                    }`}
                  >
                    {trade.direction}
                  </span>
                ),
              },
              {
                label: 'P/L',
                value: (
                  <span className={`font-semibold ${pnlColor(trade.profit_loss)}`}>
                    {trade.profit_loss !== null && trade.profit_loss !== undefined
                      ? `$${trade.profit_loss.toFixed(2)}`
                      : '—'}
                  </span>
                ),
              },
              {
                label: 'Opened',
                value: <span className="text-slate-400">{formatDate(trade.open_time)}</span>,
              },
              {
                label: 'Closed',
                value: <span className="text-slate-400">{formatDate(trade.close_time)}</span>,
              },
              {
                label: 'Lot',
                value: <span className="text-slate-300">{trade.lot_size}</span>,
              },
              {
                label: 'Reason',
                value: <span className="text-slate-400 text-xs">{formatReason(trade.close_reason)}</span>,
              },
            ]}
          />
        ))}
      </div>

      {/* Desktop table view */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-700/50">
              <th className="pb-3 font-medium">Ticket</th>
              <th className="pb-3 font-medium">Symbol</th>
              <th className="pb-3 font-medium">Direction</th>
              <th className="pb-3 font-medium">Open</th>
              <th className="pb-3 font-medium">Close</th>
              <th className="pb-3 font-medium text-right">Lot</th>
              <th className="pb-3 font-medium text-right">P/L</th>
              <th className="pb-3 font-medium">Reason</th>
              <th className="pb-3 font-medium text-right">Magic</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((trade) => (
              <tr
                key={trade.ticket}
                className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
              >
                <td className="py-3 text-slate-300">#{trade.ticket}</td>
                <td className="py-3 font-medium text-white">{trade.symbol}</td>
                <td className="py-3">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      trade.direction === 'BUY'
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'bg-red-500/10 text-red-400'
                    }`}
                  >
                    {trade.direction}
                  </span>
                </td>
                <td className="py-3 text-slate-400">{formatDate(trade.open_time)}</td>
                <td className="py-3 text-slate-400">{formatDate(trade.close_time)}</td>
                <td className="py-3 text-right text-slate-300">{trade.lot_size}</td>
                <td className={`py-3 text-right font-semibold ${pnlColor(trade.profit_loss)}`}>
                  {trade.profit_loss !== null && trade.profit_loss !== undefined
                    ? `$${trade.profit_loss.toFixed(2)}`
                    : '—'}
                </td>
                <td className="py-3 text-slate-400 text-xs">{formatReason(trade.close_reason)}</td>
                <td className="py-3 text-right text-slate-500 text-xs">{trade.magic_number || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TradeTable;
